import React, { useState, useRef, useEffect } from "react";
import CropControls from "../../CropControl/CropControl";
import FileUpload from "../../Inputs/FileUpload/FileUpload";
// import RaritySelector from "../../Inputs/RaritySelector/RaritySelector";
import styles from "./CreateOrUpdateCards.module.css";
import ColorSelector from "../../Inputs/ColorSelector/ColorSelector";
import greenOrbs from "../../../assets/cards/quality/greenOrbs.png";
import blueOrbs from "../../../assets/cards/quality/blueOrbs.png";
import purpleOrbs from "../../../assets/cards/quality/purpleOrbs.png";
import redOrbs from "../../../assets/cards/quality/redOrbs.png";
import goldOrbs from "../../../assets/cards/quality/goldOrbs.png";
import { CardData, generateCard } from "../../../services/CardServices";
import { useCreatorCardsContext } from "../../../context/Creator/CreatorCardsProvider";
import { rarityEnum } from "../../../enums/rarityEnum";
import { GenerateCardData } from "../../../services/CardServices";
import { orbEnum } from "../../../enums/orbEnum";
import globalStyles from "../../../styles/globalStyles.module.css";
import { useAlert } from "../../../context/AlertProvider";
import LoadingIcon from "../../CssIcon/Loading/LoadingIcon";
import { useTranslation } from "react-i18next";

type CardDataProps = {
  card?: CardData;
};

const CreateOrUpdateCards: React.FC<CardDataProps> = ({ card }) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string>(card?.image_id || "");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoImage, setLogoImage] = useState<string>(
    card?.logo_id || "/images/logo/logo.svg"
  );
  const [borderColor, setBorderColor] = useState<string>(
    card?.border_color || "#FFFFFF"
  );
  const [cardName, setCardName] = useState<string>(card?.name || "");
  const storedUser = localStorage.getItem("user");
  var usernameCredit = storedUser ? "@" + JSON.parse(storedUser).username : "";
  const [title, settitle] = useState<string>(card?.title || "");
  const [cardDescription, setCardDescription] = useState<string>(
    card?.description || ""
  );
  const [currentOrbIndex, setCurrentOrbIndex] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [cardWidth, setCardWidth] = useState<number>(0);
  const [textColor, setTextColor] = useState<string | null>(null);
  const [rarity, setRarity] = useState<rarityEnum>(rarityEnum.common);
  const [isBlured, setIsBlured] = useState<boolean>(false);
  const [cropData, setCropData] = useState<{
    offset: { x: number; y: number };
    scale: number;
  }>({ offset: { x: 0, y: 0 }, scale: 0 });
  const [isNsfw, setIsNsfw] = useState<boolean>(false);
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  const { cardsState } = useCreatorCardsContext();

  // const navigate = useNavigate();

  useEffect(() => {
    const initSetLogoFile = async () => {
      try {
        const storedUser = localStorage.getItem("user");
        const parsedUser = storedUser ? JSON.parse(storedUser) : null;
        var userProfilePicture =
          parsedUser?.profile_picture_url ?? "/images/logo/logo.svg";

        setLogoImage(userProfilePicture);
      } catch (error) {
        console.error("Error fetching the logo:", error);
      }
    };

    initSetLogoFile();

    const mediaQuery = window.matchMedia("(max-width: 800px)");
    var resizeValue = 0.3;
    if (mediaQuery.matches) {
      resizeValue = 0.9;
    }

    const updateCardWidth = () => {
      const width = window.innerWidth * resizeValue;
      setCardWidth(width);
    };

    updateCardWidth();

    window.addEventListener("resize", updateCardWidth);
    return () => {
      window.removeEventListener("resize", updateCardWidth);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cropContainerRef = useRef<HTMLDivElement>(null);

  const orbImages = [
    greenOrbs,
    blueOrbs,
    purpleOrbs,
    redOrbs,
    goldOrbs,
    "none",
  ];

  const orbImageDictionary: Record<string, orbEnum> = {
    [greenOrbs]: orbEnum.green,
    [blueOrbs]: orbEnum.blue,
    [purpleOrbs]: orbEnum.purple,
    [redOrbs]: orbEnum.red,
    [goldOrbs]: orbEnum.gold,
    none: orbEnum.none,
  };

  const handleLogoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        setIsUploading(true);
        const previewURL = URL.createObjectURL(file);
        setLogoFile(file);
        setLogoImage(previewURL);
      } catch (error) {
        console.error("Failed to upload logo image:", error);
        showAlert(t("alert.cards.update.logo.error"));
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleFileChange = (file: File) => {
    setImageFile(file);
    const previewURL = URL.createObjectURL(file);
    setImageSrc(previewURL);
  };

  const handleBorderColorChange = (newBorderColor: string) =>
    setBorderColor(newBorderColor);
  const handleCardNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setCardName(e.target.value);
  const handletitleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    settitle(e.target.value);
  const handleCardDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => setCardDescription(e.target.value);

  const handleOrbClick = () => {
    setCurrentOrbIndex((prevIndex) => (prevIndex + 1) % orbImages.length);
  };

  const handleCreateCard = async () => {
    if (!imageFile) {
      showAlert(t("alert.cards.update.logo.error"));

      showAlert(t("alert.cards.create.error.noFileProvided"));
      return;
    }

    if (cardName === "") {
      showAlert(t("alert.cards.create.error.noName"));
      return;
    }

    setIsUploading(true);

    try {
      setTextColor(null); //TODO temp for build error
      setRarity(rarityEnum.common); //TODO temp for build error
      const generateCardData: Omit<
        GenerateCardData,
        | "id"
        | "created_at"
        | "updated_at"
        | "blurred_image_id"
        | "image_id"
        | "small_image_id"
        | "logo_id"
        | "is_in_store"
        | "price"
        | "booster_id"
        | "blur_level"
      > = {
        image_file: new Blob([imageFile], { type: imageFile.type }),
        logo_file: logoFile
          ? new Blob([logoFile], { type: logoFile.type })
          : null,
        name: cardName,
        text_color: textColor,
        border_color: borderColor,
        title: title,
        description: cardDescription,
        orb: orbImageDictionary[orbImages[currentOrbIndex]],
        credit: usernameCredit,
        offset_x: cropData.offset.x,
        offset_y: cropData.offset.y,
        width: cardWidth,
        zoom: cropData.scale,
        rarity: rarity,
      };

      const newCard = await generateCard(generateCardData);

      newCard.booster_image = null;

      cardsState.setData((prev) => [newCard, ...prev]);
      cardsState.setFilteredData((prev) => [newCard, ...prev]);
      showAlert(t("alert.cards.create.success"));
      // navigate(-1);
      setImageFile(null);
      setImageSrc("");
    } catch (error) {
      showAlert(t("alert.cards.create.error.default"));
      setIsUploading(false);
    }
    setIsUploading(false);
  };

  const handleCropData = (
    newOffset: { x: number; y: number },
    newScale: number
  ) => {
    setCropData({
      offset: newOffset,
      scale: newScale,
    });
  };

  const toogleBlur = () => {
    setIsBlured(!isBlured);
  };

  return (
    <>
      <div className={globalStyles.pageTitle}>{t("cards.title")}</div>

      {isUploading && <LoadingIcon />}
      <div className={styles.container}>
        <div className={styles.inputContainer}>
          <div>
            <FileUpload
              onFileChange={(file: File) => handleFileChange(file)}
              handleIsNsfw={(res: boolean) => {
                setIsNsfw(res);
              }}
            />
            {/* <RaritySelector
              onRarityChange={(color: string | null, rarity: rarityEnum) => {
                setTextColor(color);
                setRarity(rarity);
              }}
            /> */}
            {/* Store the gradient instead of recalculating it each time */}
            <ColorSelector
              imageSrc={imageSrc ? imageSrc : ""}
              color={borderColor}
              onColorChange={handleBorderColorChange}
              showSolid={!textColor}
            />
            <div className="d-flex align-items-center gap-2">
              <label>{t("cards.input.previewBlur")}</label>
              <button className={globalStyles.button} onClick={toogleBlur}>
                {!isBlured ? t("enable") : t("disable")}
              </button>
            </div>
            <button
              onClick={handleCreateCard}
              className={`${globalStyles.button} ${styles.createButton}`}
              disabled={isUploading || isNsfw}
            >
              {isNsfw
                ? t("nsfwDetected")
                : isUploading
                ? t("uploading")
                : t("create")}
            </button>
          </div>
        </div>

        <div
          className={styles.cropContainer}
          ref={cropContainerRef}
          style={{
            // width: 756 + "px",
            // height: 756 * 1.4 + "px",
            // fontSize: "1px",
            width: cardWidth + "px",
            height: cardWidth * 1.4 + "px",
            fontSize: cardWidth / 750 + "px",
            filter: isBlured ? "blur(5px)" : "",
          }}
        >
          <div className={styles.header}>
            <label htmlFor="logo-upload">
              <div
                className={`${styles.parentLogoBorder}`}
                style={{
                  background:
                    borderColor === "transparent"
                      ? "transparent"
                      : textColor ?? borderColor,
                  backgroundImage:
                    borderColor === "transparent"
                      ? "none"
                      : textColor ?? borderColor,
                }}
              >
                <div className={styles.logoBorder}>
                  <img
                    src={logoImage}
                    alt="logo"
                    className={styles.logoImage}
                  />
                </div>
              </div>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              style={{ display: "none" }}
              id="logo-upload"
            />
            <div className={styles.cardNameInputContainer}>
              <input
                type="text"
                value={cardName}
                onChange={handleCardNameChange}
                className={`${styles.cardName} ${
                  textColor ? styles.rarityEnabledText : styles.textShadow
                }`}
                style={{
                  backgroundImage: textColor ?? "none",
                }}
                maxLength={28}
                placeholder={t("cards.input.namePlaceholder")}
              />
            </div>
          </div>

          <div className={styles.titleInputContainer}>
            <input
              type="text"
              value={title}
              onChange={handletitleChange}
              spellCheck="false"
              className={`${styles.titleInput} ${
                textColor ? styles.rarityEnabledText : styles.textShadow
              }`}
              style={{
                backgroundImage: textColor ?? "none",
              }}
              maxLength={36}
              placeholder={t("cards.input.titlePlaceholder")}
            />
          </div>
          <div
            className={styles.cardDescriptionInputContainer}
            style={{
              backgroundColor:
                cardDescription === "" ? "transparent" : "rgb(0,0,0,0.1)",
            }}
          >
            <textarea
              value={cardDescription}
              spellCheck="false"
              onChange={handleCardDescriptionChange}
              className={`${styles.cardDescriptionInput} ${
                textColor ? styles.rarityEnabledText : styles.textShadow
              }`}
              style={{
                backgroundImage: textColor ?? "none",
              }}
              placeholder={t("cards.input.descriptionPlaceholder")}
            />
          </div>

          <div className={styles.rarityPosition} onClick={handleOrbClick}>
            {orbImages[currentOrbIndex] !== "none" && (
              <div
                className={`${styles.parentRarityBorder}`}
                style={{
                  background:
                    borderColor === "transparent"
                      ? "transparent"
                      : textColor ?? borderColor,
                  backgroundImage:
                    borderColor === "transparent"
                      ? "none"
                      : textColor ?? borderColor,
                  boxShadow:
                    borderColor === "transparent"
                      ? "5px 5px 5px rgba(0, 0, 0, 0.3)"
                      : "none",
                }}
              >
                <div className={styles.rarityLogoBorder}>
                  <img
                    src={orbImages[currentOrbIndex]}
                    alt="orb"
                    className={styles.rarityImage}
                  />
                </div>
              </div>
            )}
          </div>
          <div
            className={`${styles.usernameCredit} ${
              textColor ? styles.rarityEnabledText : styles.textShadow
            }`}
            style={{
              backgroundImage: textColor ?? "none",
            }}
          >
            {usernameCredit}
          </div>
          <div
            className={`
          ${
            borderColor !== "transparent"
              ? styles.imageWrapper
              : styles.borderlessImageWrapper
          } 
          `}
            style={{
              backgroundImage: textColor ?? "none",
              background: textColor ?? borderColor,
            }}
          >
            <div
              className={`${
                borderColor !== "transparent" && styles.innerBorderRadius
              }`}
              style={{ height: "100%", width: "auto" }}
            >
              <CropControls
                imageSrc={imageSrc}
                handleCropData={handleCropData}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateOrUpdateCards;
